import { Ref, ref, ComputedRef, computed } from 'vue'
import { useLoading, Loading } from '@/composable/useLoading'
import { VehicleWithdrawalReason } from '@/types/vehicle-withdrawal-reason'
import { getVehicleWithdrawalReasons } from '@/api/vehicle-withdrawal-reason/getVehicleWithdrawalReasons'
import { deleteVehicleWithdrawalReason } from '@/api/vehicle-withdrawal-reason/deleteVehicleWithdrawalReason'
import { createVehicleWithdrawalReason } from '@/api/vehicle-withdrawal-reason/createVehicleWithdrawalReason'
import { updateVehicleWithdrawalReason } from '@/api/vehicle-withdrawal-reason/updateVehicleWithdrawalReason'

interface UseVehicleWithdrawalReason {
  data: Ref<VehicleWithdrawalReason[]>
  fetchAll: () => Promise<VehicleWithdrawalReason[]>
  remove: (uuid: string) => Promise<null>
  add: (body: VehicleWithdrawalReason) => Promise<VehicleWithdrawalReason>
  update: (body: VehicleWithdrawalReason) => Promise<VehicleWithdrawalReason>
  loading: ComputedRef<Loading>
}

const data = ref<VehicleWithdrawalReason[]>([])
const { state } = useLoading()

export function useVehicleWithdrawalReason(
  initFetchAll?: boolean
): UseVehicleWithdrawalReason {
  async function fetchAll() {
    state.getAll = true
    return new Promise<VehicleWithdrawalReason[]>((resolve, reject) => {
      getVehicleWithdrawalReasons()
        .then(({ data: vehicleWithdrawalReasons }) => {
          data.value = vehicleWithdrawalReasons
          resolve(vehicleWithdrawalReasons)
        })
        .catch(reject)
        .finally(() => {
          state.getAll = false
        })
    })
  }

  async function remove(uuid: string) {
    state.delete = true
    return new Promise<null>((resolve, reject) => {
      deleteVehicleWithdrawalReason(uuid)
        .then(() => {
          data.value = data.value.filter((item) => item.uuid !== uuid)
          resolve(null)
        })
        .catch(reject)
        .finally(() => {
          state.delete = false
        })
    })
  }

  async function add(body: VehicleWithdrawalReason) {
    state.create = true
    return new Promise<VehicleWithdrawalReason>((resolve, reject) => {
      createVehicleWithdrawalReason(body)
        .then(({ data: vehicleWithdrawalReason }) => {
          data.value.push(vehicleWithdrawalReason)
          resolve(vehicleWithdrawalReason)
        })
        .catch(reject)
        .finally(() => {
          state.create = false
        })
    })
  }

  async function update(body: VehicleWithdrawalReason) {
    state.update = true
    return new Promise<VehicleWithdrawalReason>((resolve, reject) => {
      updateVehicleWithdrawalReason(body)
        .then(({ data: vehicleWithdrawalReason }) => {
          data.value = data.value.map((item) => {
            if (item.uuid === vehicleWithdrawalReason.uuid) {
              return {
                ...item,
                ...vehicleWithdrawalReason,
              }
            }

            return item
          })
          resolve(vehicleWithdrawalReason)
        })
        .catch(reject)
        .finally(() => {
          state.update = false
        })
    })
  }

  if (initFetchAll) {
    fetchAll()
  }

  return {
    data,
    fetchAll,
    add,
    remove,
    update,
    loading: computed(() => state),
  }
}
